import React, { useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';

interface LandingPageProps {
  onStartPlanning: () => void;
  shouldExit?: boolean;
  onExitComplete?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ 
  onStartPlanning,
  shouldExit = false,
  onExitComplete
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const posthog = usePostHog();

  // Track landing page view on mount
  useEffect(() => {
    posthog?.capture('landing_page_viewed');
  }, [posthog]);

  // Handle external exit trigger
  useEffect(() => {
    if (shouldExit && !isExiting) {
      setIsExiting(true);
      setTimeout(() => {
        onExitComplete?.();
      }, 600); // Match animation duration
    }
  }, [shouldExit, isExiting, onExitComplete]);

  const handleStartPlanningClick = () => {
    posthog?.capture('start_now_clicked');
    setIsExiting(true);
    setTimeout(() => {
      onStartPlanning();
    }, 600); // Match animation duration
  };

  return (
    <div className="fixed inset-0 flex items-end justify-center pb-24 pointer-events-none z-40">
      <div 
        className={`text-center max-w-6xl px-8 pointer-events-auto transition-all duration-500 ease-out ${
          isExiting 
            ? 'opacity-0 translate-y-24' 
            : 'opacity-100 translate-y-0'
        }`}
        style={{
          animation: 'landingFadeIn 0.6s ease-out'
        }}
      >
          {/* Hero Heading */}
          <h1 
            className="text-white mb-6 leading-[100%] sm:leading-[120px]"
            style={{ 
              fontFamily: "'Rethink Sans', sans-serif",
              fontSize: '80px',
              letterSpacing: '-0.05em',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'  
            }}
          >
            Start moving your day
          </h1>
          
          {/* Subtitle */}
          <p 
            className="text-lg md:text-xl text-gray-100 mb-8 max-w-2xl mx-auto"
            style={{ 
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)'
            }}
          >
            Capture your thoughts, time your focus, start vibing.
          </p>
          
          {/* CTA Button */}
          <div className="flex justify-center">
            <button
              onClick={handleStartPlanningClick}
              className="px-8 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              aria-label="Start planning your tasks"
            >
              Start Now →
            </button>
          </div>
        </div>
    </div>
  );
};

export default LandingPage;

